import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { ShieldCheck, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutPage() {
  const { cart, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [address, setAddress] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    pincode: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAddress((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const required = ["name", "phone", "address_line1", "city", "state", "pincode"];
    for (const field of required) {
      if (!address[field]) {
        toast.error(`Please fill ${field.replace("_", " ")}`);
        return false;
      }
    }
    if (address.pincode.length !== 6) {
      toast.error("Please enter a valid 6-digit pincode");
      return false;
    }
    return true;
  };

  const handlePayment = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      // Create order
      const orderData = {
        items: cart.items.map((item) => ({
          product_id: item.product_id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          size: item.size,
          color: item.color,
          image: item.image,
        })),
        address,
        payment_method: "razorpay",
        total_amount: cart.total,
      };

      const orderResponse = await axios.post(`${API}/orders`, orderData);
      const orderId = orderResponse.data.order_id;

      // Create Razorpay order
      const paymentResponse = await axios.post(
        `${API}/payment/create-order?amount=${cart.total}&order_id=${orderId}`
      );

      const { razorpay_order_id, amount, key_id } = paymentResponse.data;

      // Load Razorpay script if not loaded
      if (!window.Razorpay) {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        document.body.appendChild(script);
        await new Promise(resolve => script.onload = resolve);
      }

      // Initialize Razorpay
      const options = {
        key: key_id,
        amount: amount,
        currency: "INR",
        name: "OGAS",
        description: "T-Shirt Purchase",
        order_id: razorpay_order_id,
        handler: async (response) => {
          try {
            // Verify payment
            await axios.post(`${API}/payment/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              order_id: orderId,
            });

            toast.success("Payment successful! Order placed.");
            clearCart();
            navigate("/profile");
          } catch (error) {
            toast.error("Payment verification failed");
          }
        },
        prefill: {
          name: address.name,
          email: user?.email,
          contact: address.phone,
        },
        theme: {
          color: "#1A4D2E",
        },
        modal: {
          ondismiss: function() {
            setLoading(false);
            toast.error("Payment cancelled");
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error("Payment error:", error);
      toast.error(error.response?.data?.detail || "Failed to initiate payment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    navigate("/auth?redirect=/checkout");
    return null;
  }

  if (!cart.items || cart.items.length === 0) {
    navigate("/cart");
    return null;
  }

  return (
    <div className="min-h-screen bg-cotton-100 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-heading text-3xl md:text-4xl font-bold mb-8" data-testid="checkout-title">
          Checkout
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Shipping Address */}
          <div className="lg:col-span-2">
            <div className="bg-white p-6 rounded-lg">
              <h2 className="font-heading text-xl font-bold mb-6">Shipping Address</h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={address.name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    className="mt-1"
                    data-testid="name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={address.phone}
                    onChange={handleInputChange}
                    placeholder="10-digit mobile number"
                    className="mt-1"
                    data-testid="phone-input"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="address_line1">Address Line 1 *</Label>
                  <Input
                    id="address_line1"
                    name="address_line1"
                    value={address.address_line1}
                    onChange={handleInputChange}
                    placeholder="House no, Building, Street"
                    className="mt-1"
                    data-testid="address1-input"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="address_line2">Address Line 2</Label>
                  <Input
                    id="address_line2"
                    name="address_line2"
                    value={address.address_line2}
                    onChange={handleInputChange}
                    placeholder="Landmark, Area (optional)"
                    className="mt-1"
                    data-testid="address2-input"
                  />
                </div>
                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    name="city"
                    value={address.city}
                    onChange={handleInputChange}
                    placeholder="Enter city"
                    className="mt-1"
                    data-testid="city-input"
                  />
                </div>
                <div>
                  <Label htmlFor="state">State *</Label>
                  <Input
                    id="state"
                    name="state"
                    value={address.state}
                    onChange={handleInputChange}
                    placeholder="Enter state"
                    className="mt-1"
                    data-testid="state-input"
                  />
                </div>
                <div>
                  <Label htmlFor="pincode">Pincode *</Label>
                  <Input
                    id="pincode"
                    name="pincode"
                    value={address.pincode}
                    onChange={handleInputChange}
                    placeholder="6-digit pincode"
                    maxLength={6}
                    className="mt-1"
                    data-testid="pincode-input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white p-6 rounded-lg sticky top-24">
              <h2 className="font-heading text-xl font-bold mb-6">Order Summary</h2>

              <div className="space-y-4 mb-6 max-h-64 overflow-y-auto">
                {cart.items.map((item, index) => (
                  <div key={index} className="flex gap-3">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-16 h-16 object-cover rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm line-clamp-1">{item.name}</p>
                      <p className="text-muted-foreground text-xs">
                        {item.size} | {item.color} | Qty: {item.quantity}
                      </p>
                      <p className="text-primary font-medium text-sm">
                        ₹{(item.price * item.quantity).toLocaleString("en-IN")}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t pt-4 space-y-2 mb-6">
                <div className="flex justify-between text-muted-foreground">
                  <span>Subtotal</span>
                  <span>₹{cart.total.toLocaleString("en-IN")}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Shipping</span>
                  <span className="text-primary">Free</span>
                </div>
                <div className="flex justify-between font-bold text-lg pt-2 border-t">
                  <span>Total</span>
                  <span data-testid="checkout-total">₹{cart.total.toLocaleString("en-IN")}</span>
                </div>
              </div>

              <Button
                onClick={handlePayment}
                disabled={loading}
                className="w-full bg-primary hover:bg-primary/90 rounded-full"
                size="lg"
                data-testid="pay-now-btn"
              >
                {loading ? (
                  "Processing..."
                ) : (
                  <>
                    <Lock className="w-4 h-4 mr-2" />
                    Pay ₹{cart.total.toLocaleString("en-IN")}
                  </>
                )}
              </Button>

              <div className="flex items-center justify-center gap-2 mt-4 text-muted-foreground text-sm">
                <ShieldCheck className="w-4 h-4" />
                <span>Secure payment via Razorpay</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
